import { Err, Ok, Result } from '@gametailors/web-core/classes';
import {
  LpRidLeeslijstAssignment,
  LpRidLeeslijstConfig,
  LpRidWisselklankenAssignment,
  LpRidWisselklankenConfig,
  LpRidWisselklankenUnit,
  LpSchema,
} from '../../../typings/schema';
import { AnnotationUtils } from '../../utils/annotation.utils';

type LpInstance = LpSchema['all'];

/** One generator, and the data type of the instance it returns. */
export interface GeneratorStep<T extends LpInstance, TFrom = void> {
  generatorId: string;
  dataType: T['dataType'];
  /** Fills in what the generator leaves out, or gives the reason the result can't be played. */
  complete?: (value: T, from: TFrom) => Result<T, string>;
}

/**
 * The two generators behind a mechanic: one recommends a config (settings and which content), the other picks the
 * content for that config (the assignment).
 */
export interface MechanicGenerators<TConfig extends LpInstance, TAssignment extends LpInstance> {
  config: GeneratorStep<TConfig>;
  assignment: GeneratorStep<TAssignment, TConfig>;
}

const MISSING_WORDS = 'it refers to words that are not in the data package';

// The schema default, also for configs saved before `unit` existed.
const DEFAULT_WISSELKLANKEN_UNIT: LpRidWisselklankenUnit = 'KLANK';

export const wisselklankenGenerators: MechanicGenerators<LpRidWisselklankenConfig, LpRidWisselklankenAssignment> = {
  config: {
    generatorId: 'wisselklanken_config',
    dataType: 'RidWisselklankenConfig',
    complete: (config) => Ok({ ...config, unit: config.unit ?? DEFAULT_WISSELKLANKEN_UNIT }),
  },
  assignment: {
    generatorId: 'wisselklanken_assignment',
    dataType: 'RidWisselklankenAssignment',
    complete: (assignment, config) => {
      if (!assignment.words.every(Boolean)) {
        return Err(MISSING_WORDS);
      }

      const unit = assignment.unit ?? config.unit ?? DEFAULT_WISSELKLANKEN_UNIT;
      // a single klankvoet would be a single tile, which leaves nothing to switch between
      const words = unit === 'KLANKVOET' ? assignment.words.filter((w) => AnnotationUtils.parse(w.annotatie).length > 1) : assignment.words;
      if (words.length === 0) {
        return Err(`none of its ${assignment.words.length} words has more than one klankvoet`);
      }

      return Ok({ ...assignment, words, unit });
    },
  },
};

export const leeslijstGenerators: MechanicGenerators<LpRidLeeslijstConfig, LpRidLeeslijstAssignment> = {
  config: {
    generatorId: 'leeslijst_config',
    dataType: 'RidLeeslijstConfig',
  },
  assignment: {
    generatorId: 'leeslijst_assignment',
    dataType: 'RidLeeslijstAssignment',
    complete: (assignment) => (assignment.mode.words.every((column) => column.words.every(Boolean)) ? Ok(assignment) : Err(MISSING_WORDS)),
  },
};
